import { useEffect, useRef, useState, useCallback, useMemo, CSSProperties } from "react";
import { Spin } from "antd";
import * as XLSX from "xlsx";

interface ExcelPreviewProps {
  url: string;
}

type CellValue = string | number | boolean | null;
type SheetData = CellValue[][];

interface ColMeta {
  width: number; // px
}

// Excel-style column label: 0→A, 25→Z, 26→AA …
function colLabel(i: number): string {
  let s = "";
  let n = i + 1;
  while (n > 0) {
    n--;
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26);
  }
  return s;
}

function formatCell(val: CellValue): string {
  if (val === null || val === undefined || val === "") return "";
  if (typeof val === "boolean") return val ? "TRUE" : "FALSE";
  if (typeof val === "number") {
    return parseFloat(val.toPrecision(10)).toLocaleString("en-US", { maximumFractionDigits: 10 });
  }
  return String(val);
}

function isNumeric(val: CellValue): boolean {
  return typeof val === "number";
}

const ROW_HEIGHT = 22;
const HEADER_H = 24;
const ROW_NUM_W = 52;
const MIN_COL_W = 60;
const MAX_COL_W = 320;
const OVERSCAN = 8;

export const ExcelPreview = ({ url }: ExcelPreviewProps) => {
  const [sheets, setSheets] = useState<Record<string, SheetData>>({});
  const [sheetNames, setNames] = useState<string[]>([]);
  const [active, setActive] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sel, setSel] = useState<{ r: number; c: number } | null>(null);
  const [colWidths, setColWidths] = useState<Record<string, ColMeta[]>>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewH, setViewH] = useState(600);
  const dragRef = useRef<{ ci: number; startX: number; startW: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setSel(null);
    setScrollTop(0);

    (async () => {
      try {
        const res = await fetch(url);
        const buf = await res.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array", cellDates: false, raw: true });

        const parsed: Record<string, SheetData> = {};
        const widths: Record<string, ColMeta[]> = {};

        for (const name of wb.SheetNames) {
          const raw = XLSX.utils.sheet_to_json<CellValue[]>(wb.Sheets[name], {
            header: 1,
            defval: null,
            raw: true,
          }) as SheetData;
          parsed[name] = raw;

          const maxCols = Math.max(0, ...raw.map((r) => r.length));
          widths[name] = Array.from({ length: maxCols }, (_, ci) => {
            const maxLen = raw.reduce((m, row) => {
              return Math.max(m, formatCell(row[ci]).length);
            }, colLabel(ci).length);
            return { width: Math.min(Math.max(maxLen * 7.5 + 16, MIN_COL_W), MAX_COL_W) };
          });
        }

        if (cancelled) return;
        setSheets(parsed);
        setNames(wb.SheetNames);
        setColWidths(widths);
        setActive(wb.SheetNames[0] ?? "");
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setViewH(el.clientHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rows = useMemo(() => sheets[active] ?? [], [sheets, active]);
  const cols = useMemo(() => colWidths[active] ?? [], [colWidths, active]);
  const maxCols = cols.length;
  const totalH = rows.length * ROW_HEIGHT;
  const totalW = ROW_NUM_W + cols.reduce((s, c) => s + c.width, 0);

  const firstRow = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const lastRow = Math.min(rows.length - 1, Math.ceil((scrollTop + viewH) / ROW_HEIGHT) + OVERSCAN);
  const visibleRows = rows.slice(firstRow, lastRow + 1);

  const selVal = sel ? formatCell(rows[sel.r]?.[sel.c] ?? null) : "";

  const onResizeStart = useCallback(
    (e: React.MouseEvent, ci: number) => {
      e.preventDefault();
      dragRef.current = { ci, startX: e.clientX, startW: colWidths[active]?.[ci]?.width ?? 100 };
      const onMove = (ev: MouseEvent) => {
        const nw = Math.max(MIN_COL_W, dragRef.current!.startW + ev.clientX - dragRef.current!.startX);
        setColWidths((p) => ({ ...p, [active]: (p[active] ?? []).map((m, i) => (i === ci ? { width: nw } : m)) }));
      };
      const onUp = () => {
        dragRef.current = null;
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [active, colWidths],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!sel) return;
      const map: Record<string, [number, number]> = {
        ArrowUp: [-1, 0],
        ArrowDown: [1, 0],
        ArrowLeft: [0, -1],
        ArrowRight: [0, 1],
        Tab: [0, 1],
        Enter: [1, 0],
      };
      const d = map[e.key];
      if (!d) return;
      e.preventDefault();
      const nr = Math.max(0, Math.min(rows.length - 1, sel.r + d[0]));
      const nc = Math.max(0, Math.min(maxCols - 1, sel.c + d[1]));
      setSel({ r: nr, c: nc });
      if (scrollRef.current) {
        const top = nr * ROW_HEIGHT;
        if (top < scrollRef.current.scrollTop) scrollRef.current.scrollTop = top;
        if (top + ROW_HEIGHT > scrollRef.current.scrollTop + viewH) scrollRef.current.scrollTop = top + ROW_HEIGHT - viewH;
      }
    },
    [sel, rows.length, maxCols, viewH],
  );

  if (loading)
    return (
      <div style={$center}>
        <Spin size="large" />
        <span className="mt-[10px] text-xs text-[#6e7781]">Đang đọc file Excel…</span>
      </div>
    );
  if (error)
    return (
      <div style={$center}>
        <span className="text-[40px]">⚠️</span>
        <span className="text-[13px] text-[#6e7781]">Không thể đọc file Excel</span>
      </div>
    );
  if (rows.length === 0)
    return (
      <div style={$center}>
        <span className="text-[13px] text-[#6e7781]">Sheet này trống</span>
      </div>
    );

  return (
    <div
      tabIndex={0}
      onKeyDown={onKeyDown}
      className="flex flex-col h-full bg-white text-[13px] outline-none select-none font-[Calibri,'Segoe_UI',Arial,sans-serif]"
    >
      {/* formula bar */}
      <div className="flex items-center h-7 border-b border-[#d0d7de] bg-[#f8f8f8] shrink-0">
        <div className="w-[72px] shrink-0 px-2 text-xs font-semibold text-[#1f2328] border-r border-[#d0d7de] h-full flex items-center bg-white">
          {sel ? `${colLabel(sel.c)}${sel.r + 1}` : ""}
        </div>
        <div className="w-7 shrink-0 flex items-center justify-center text-[#888] text-xs italic border-r border-[#d0d7de] h-full">fx</div>
        <div className="flex-1 px-[10px] text-xs text-[#1f2328] overflow-hidden truncate">{selVal}</div>
      </div>

      {/* grid */}
      <div className="flex-1 overflow-hidden flex flex-col relative">
        {/* col header row */}
        <div className="flex shrink-0 bg-[#f0f0f0] border-b border-[#c6cdd4] relative z-30">
          {/* corner */}
          <div style={{ width: ROW_NUM_W, flexShrink: 0, height: HEADER_H, borderRight: "1px solid #c6cdd4", background: "#f0f0f0" }} />
          {/* col labels — sync with body scroll */}
          <div id="xl-col-hdr" className="flex-1 overflow-hidden">
            <div style={{ display: "flex", height: HEADER_H, width: totalW - ROW_NUM_W }}>
              {cols.map((col, ci) => (
                <div
                  key={ci}
                  style={{
                    width: col.width,
                    flexShrink: 0,
                    height: HEADER_H,
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    color: sel?.c === ci ? "#1a73e8" : "#444",
                    background: sel?.c === ci ? "#ddeeff" : "#f0f0f0",
                    borderRight: "1px solid #d0d7de",
                  }}
                >
                  {colLabel(ci)}
                  <div onMouseDown={(e) => onResizeStart(e, ci)} className="absolute right-0 top-0 w-1 h-full cursor-col-resize" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* scrollable body */}
        <div
          ref={scrollRef}
          onScroll={(e) => {
            setScrollTop(e.currentTarget.scrollTop);
            const hdr = document.getElementById("xl-col-hdr");
            if (hdr) hdr.scrollLeft = e.currentTarget.scrollLeft;
          }}
          className="flex-1 overflow-auto"
        >
          <div style={{ height: totalH, width: totalW, position: "relative" }}>
            {/* row number gutter — sticky left */}
            <div style={{ position: "sticky", left: 0, zIndex: 2, float: "left", width: ROW_NUM_W }}>
              {visibleRows.map((_, i) => {
                const ri = firstRow + i;
                const isSel = sel?.r === ri;
                return (
                  <div
                    key={ri}
                    style={{
                      position: "absolute",
                      top: ri * ROW_HEIGHT,
                      left: 0,
                      width: ROW_NUM_W,
                      height: ROW_HEIGHT,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      paddingRight: 6,
                      fontSize: 11,
                      color: isSel ? "#1a73e8" : "#6e7781",
                      background: isSel ? "#ddeeff" : "#f0f0f0",
                      borderRight: "1px solid #d0d7de",
                      borderBottom: "1px solid #e8ecef",
                      fontWeight: isSel ? 700 : 400,
                      boxSizing: "border-box",
                    }}
                  >
                    {ri + 1}
                  </div>
                );
              })}
            </div>

            {/* data cells */}
            <div style={{ position: "absolute", left: ROW_NUM_W, top: 0 }}>
              {visibleRows.map((row, i) => {
                const ri = firstRow + i;
                return (
                  <div key={ri} style={{ position: "absolute", top: ri * ROW_HEIGHT, left: 0, display: "flex", height: ROW_HEIGHT }}>
                    {cols.map((col, ci) => {
                      const val = row[ci] ?? null;
                      const str = formatCell(val);
                      const num = isNumeric(val);
                      const isSel = sel?.r === ri && sel?.c === ci;
                      const hlRow = sel?.r === ri;
                      const hlCol = sel?.c === ci;
                      const isFirstRow = ri === 0;

                      return (
                        <div
                          key={ci}
                          onClick={() => setSel({ r: ri, c: ci })}
                          title={str.length > 20 ? str : undefined}
                          style={{
                            width: col.width,
                            flexShrink: 0,
                            height: ROW_HEIGHT,
                            padding: "0 5px",
                            boxSizing: "border-box",
                            display: "flex",
                            alignItems: "center",
                            overflow: "hidden",
                            cursor: "cell",
                            position: "relative",
                            borderRight: "1px solid #e8ecef",
                            borderBottom: "1px solid #e8ecef",
                            background: isSel ? "#e8f0fe" : hlRow || hlCol ? "#f0f7ff" : "#fff",
                            outline: isSel ? "2px solid #1a73e8" : "none",
                            outlineOffset: -1,
                            zIndex: isSel ? 2 : 0,
                            fontWeight: isFirstRow ? 700 : 400,
                          }}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              width: "100%",
                              textAlign: num ? "right" : "left",
                              fontSize: 12,
                              color: num ? "#0f4c81" : "#1f2328",
                            }}
                          >
                            {str}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* sheet tabs + status bar */}
      <div className="shrink-0 flex items-stretch border-t border-[#d0d7de] bg-[#f0f0f0] min-h-[30px] overflow-x-auto">
        {sheetNames.map((name) => {
          const isAct = name === active;
          return (
            <button
              key={name}
              onClick={() => {
                setActive(name);
                setSel(null);
                setScrollTop(0);
                if (scrollRef.current) scrollRef.current.scrollTop = 0;
              }}
              style={{
                padding: "0 16px",
                fontSize: 12,
                cursor: "pointer",
                border: "none",
                borderRight: "1px solid #d0d7de",
                background: isAct ? "#fff" : "transparent",
                color: isAct ? "#1a73e8" : "#555",
                fontWeight: isAct ? 700 : 400,
                borderTop: isAct ? "2px solid #1a73e8" : "2px solid transparent",
                whiteSpace: "nowrap",
                flexShrink: 0,
                fontFamily: "inherit",
              }}
            >
              📋 {name}
            </button>
          );
        })}
        <div className="ml-auto px-4 flex items-center gap-5 text-[11px] text-[#6e7781] shrink-0 whitespace-nowrap">
          {sel && isNumeric(rows[sel.r]?.[sel.c] ?? null) && (
            <span>
              Tổng: <strong className="text-[#1f2328]">{(rows[sel.r][sel.c] as number).toLocaleString("en-US")}</strong>
            </span>
          )}
          <span>
            {rows.length} hàng · {maxCols} cột
          </span>
        </div>
      </div>
    </div>
  );
};

const $center: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  gap: 12,
};
